"use client";

import { c as _c } from "react/compiler-runtime";
import { notificationsControllerGetMyNotifications, notificationsControllerGetMyNotificationsQueryKey, notificationsControllerGetMyNotificationsQueryOptions } from "@dc/gateway-jsonapi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

// One unread row is enough: the list endpoint returns the full filtered count in meta.total.
const UNREAD_COUNT_PARAMS = {
  filter: {
    read: false
  },
  page: {
    size: 1
  }
};
type UnreadCountResponse = {
  meta?: {
    total?: number;
  };
};
export function useUnreadNotificationCount() {
  const $ = _c(16);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = notificationsControllerGetMyNotificationsQueryKey(UNREAD_COUNT_PARAMS);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const queryKey = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ids: new Set(),
      all: false
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const readsBeforeCount = useRef(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      ...notificationsControllerGetMyNotificationsQueryOptions(UNREAD_COUNT_PARAMS),
      queryFn: async t3 => {
        const {
          signal
        } = t3;
        const response = await notificationsControllerGetMyNotifications(UNREAD_COUNT_PARAMS, {
          signal
        });
        const {
          ids,
          all
        } = readsBeforeCount.current;
        readsBeforeCount.current = {
          ids: new Set(),
          all: false
        };
        const total = all ? 0 : Math.max(0, (response.meta?.total ?? 0) - ids.size);
        return {
          ...response,
          meta: {
            ...response.meta,
            total
          }
        };
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data,
    refetch
  } = useQuery(t2);
  const unreadCount = (data as UnreadCountResponse | undefined)?.meta?.total ?? 0;
  let t3;
  if ($[3] !== queryClient) {
    t3 = () => queryClient.getQueryData(queryKey) !== undefined;
    $[3] = queryClient;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const hasCachedCount = t3;
  let t4;
  if ($[5] !== queryClient) {
    t4 = update => {
      queryClient.setQueryData(queryKey, previous => previous && {
        ...previous,
        meta: {
          ...previous.meta,
          total: update(previous.meta?.total ?? 0)
        }
      });
    };
    $[5] = queryClient;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const updateCachedTotal = t4;
  let t5;
  let t6;
  if ($[7] !== hasCachedCount || $[8] !== updateCachedTotal) {
    t5 = id => {
      if (hasCachedCount()) {
        updateCachedTotal(_temp);
      } else {
        readsBeforeCount.current.ids.add(id);
      }
    };
    t6 = () => {
      if (hasCachedCount()) {
        updateCachedTotal(_temp2);
      } else {
        readsBeforeCount.current.all = true;
      }
    };
    $[7] = hasCachedCount;
    $[8] = updateCachedTotal;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[11] !== refetch || $[12] !== t5 || $[13] !== t6 || $[14] !== unreadCount) {
    t7 = {
      unreadCount,
      decrementUnreadCount: t5,
      clearUnreadCount: t6,
      refetchUnreadCount: refetch
    };
    $[11] = refetch;
    $[12] = t5;
    $[13] = t6;
    $[14] = unreadCount;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
function _temp2() {
  return 0;
}
function _temp(total_0) {
  return Math.max(0, total_0 - 1);
}